import { createApp, reactive, watch } from 'vue';

function detectMobile(): boolean {
  if (typeof window !== 'undefined' && ((window as any).__AIMETA_IS_NATIVE__ || (window as any).AimetaNative)) {
    return true;
  }
  if (typeof window !== 'undefined' && window.chrome?.webview) {
    return false;
  }
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent || '';
    const mobileRegex = /Android|iPhone|iPad|iPod|Mobile/i;
    if (mobileRegex.test(ua)) {
      return true;
    }
  }
  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    return true;
  }
  return false;
}

const isMobile = detectMobile();

if (isMobile) {
  document.documentElement.classList.add('is-mobile');
}
import { createPinia } from 'pinia';
import ElementPlus from 'element-plus'; //全局引入
import 'element-plus/dist/index.css';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';
import App from './App.vue';
import router from './router';
import '@fontsource/nanum-gothic/korean-400.css';
import '@fontsource/nanum-gothic/korean-700.css';
import './assets/style/index.scss';
import './assets/main.css';
import '@/assets/iconfont/iconfont.css'; //引入阿里图标库
import { Swiper, SwiperSlide } from 'swiper/vue';
import IconFont from '@/components/common/IconFont.vue';
import IconSymbol from '@/components/common/IconSymbol.vue';
import UiButton from '@/components/button/UiButton.vue';
import NormalButton from '@/components/button/NormalButton.vue';
import CardBox from '@/components/common/CardBox.vue';
import CustomPopup from '@/components/common/CustomPopup.vue';
import CustomSearch from '@/components/common/CustomSearch.vue';
import CustomTip from '@/components/common/CustomTip.vue';
import CustomInput from '@/components/common/CustomInput.vue';
import CustomPopover from '@/components/common/CustomPopover.vue';
import CustomPicker from '@/components/common/CustomPicker.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import SelectIcon from '@/components/common/SelectIcon.vue';
import CustomSwitch from '@/components/common/CustomSwitch.vue';
import CustomRadio from '@/components/common/CustomRadio.vue';
import CustomSelect from '@/components/common/CustomSelect.vue';
import SelectActive from '@/components/common/SelectActive.vue';
import CustomTabsIcon from '@/components/common/CustomTabsIcon.vue';

import message from '@/utils/messageManager';
import clickOutside from '@/utils/directives/click-outside'; // 导入自定义指令
import NumberOnlyDirective from '@/utils/directives/number-only';
import methods, {
  globalMethodsPlugin,
  mergeAppConfigWithDefaults,
} from '@/utils/localMethod';
import {
  createI18nInstance,
  loadLanguageAsync,
  getCurrentLocale,
  languages as i18nLanguages,
  syncDocumentLocaleFromCode,
} from './i18n';

import { formatFileSize } from './utils/formatFileSize';
import { formatTime } from '@/utils/formatTime';

const defaultAppConfig = {
  defaultLanguage: 'zh',
  product: true,
  robot: false,
  scheduledShutdown: false,
  washManuscript: true,
  promotionScript: true,
  /** 直播间侧栏「定时引导」模块；checkpoint 可写 timedGuidance: false 关闭 */
  timedGuidance: true,
  scriptSettings: {
    freedom: true,
    fixed: true,
  },
  speechEngines: ['basic', 'premium', 'pro'],
  liveType: [
    'douyin',
    'xiaohongshu',
    'kuaishou',
    'wechat',
    'taobao',
    'pinduoduo',
    'jd',
    'tiktok',
    'douyu',
    'bilibili',
    'huya',
    'pino',
  ],
  /** 与 system_param.accountMode 一致；checkpoint 未就绪前默认 phone，避免注册页误判为邮箱分支 */
  accountMode: 'phone' as 'email' | 'phone',
  /** 系统参数 freeScriptMinChars（默认 30）；自由话术按条成段后前端不再用其做最短校验 */
  freeScriptMinChars: 30,
  /** 知识库是否允许导入 Word（system_param.knowledgeAllowImportWord，默认允许） */
  knowledgeAllowImportWord: true,
};

const appConfig: any = reactive(
  mergeAppConfigWithDefaults(defaultAppConfig, {
    ...(await methods.getLocalConfig().catch(() => ({}))),
  })
);

async function mergeCheckpointIntoAppConfig() {
  try {
    const partial = await methods.getLocalConfig();
    const merged = mergeAppConfigWithDefaults(defaultAppConfig, partial);
    Object.assign(appConfig, merged);
    applySkinFromConfig(appConfig?.skin);
  } catch {
    /* ignore */
  }
}

const applySkinFromConfig = (skin: string | undefined) => {
  if (isMobile) {
    localStorage.setItem('aimeta_skin', 'tech');
    document.documentElement.setAttribute('data-skin', 'tech');
    return;
  }
  const normalizedInput = String(skin || 'white')
    .trim()
    .toLowerCase();
  const normalized =
    normalizedInput === 'sunset' || normalizedInput === 'crimson'
      ? 'tech'
      : normalizedInput;
  const selectable = normalized === 'all';
  const selectedFromLocalRaw = localStorage.getItem('aimeta_skin');
  const selectedFromLocal =
    selectedFromLocalRaw === 'sunset' || selectedFromLocalRaw === 'crimson'
      ? 'tech'
      : selectedFromLocalRaw;
  const isValidSkin = (v: string | null) =>
    v === 'white' ||
    v === 'tech' ||
    v === 'pink' ||
    v === 'black' ||
    v === 'purple' ||
    v === 'emerald';
  const isSelectableSkin = (v: string | null) =>
    v === 'white' ||
    v === 'tech' ||
    v === 'pink' ||
    v === 'black' ||
    v === 'purple' ||
    v === 'emerald';
  // all 模式默认科技色；若已缓存则优先缓存值
  if (selectable && !isSelectableSkin(selectedFromLocal)) {
    localStorage.setItem('aimeta_skin', 'tech');
  }
  const chosen =
    selectable && isSelectableSkin(selectedFromLocal)
      ? selectedFromLocal
      : selectable
        ? 'tech'
        : normalized;
  const finalSkin = isValidSkin(chosen) ? chosen : 'white';
  document.documentElement.setAttribute('data-skin', finalSkin);
};

applySkinFromConfig(appConfig?.skin);

const app = createApp(App);

app.provide('appConfig', appConfig);
app.provide('mergeCheckpointIntoAppConfig', mergeCheckpointIntoAppConfig);

const i18n = await createI18nInstance(appConfig?.defaultLanguage);

watch(
  () => String((i18n.global.locale as any).value ?? 'zh'),
  (code) => syncDocumentLocaleFromCode(code),
  { immediate: true }
);

app.use(i18n);

declare global {
  interface Window {
    __AIMETA_RELOAD_LOCALE?: (lang?: string) => Promise<void>;
  }
}

window.__AIMETA_RELOAD_LOCALE = async (lang?: string) => {
  const code =
    lang && i18nLanguages.some((l) => l.code === lang)
      ? lang
      : getCurrentLocale();
  await loadLanguageAsync(code, { force: true });
};

// If Android native injected language pack before main.ts await resumed, apply it now
if (
  window.__AIMETA_LANG_PACK__ &&
  typeof window.__AIMETA_LANG_PACK__ === 'object' &&
  Object.keys(window.__AIMETA_LANG_PACK__).length > 0
) {
  const langCode = (window.__AIMETA_LANG as string) || 'zh';
  i18n.global.setLocaleMessage(langCode, window.__AIMETA_LANG_PACK__);
  (i18n.global.locale as any).value = langCode;
  localStorage.setItem(LANGUAGE_KEY, langCode);
  syncDocumentLocaleFromCode(langCode);
}

// 全局禁用右键菜单
document.addEventListener('contextmenu', (e) => {
  e.preventDefault();
});

// 全局注册组件
app.component('IconFont', IconFont);
app.component('IconSymbol', IconSymbol);
app.component('UiButton', UiButton);
app.component('NormalButton', NormalButton);
app.component('CardBox', CardBox);
app.component('CustomPopup', CustomPopup);
app.component('CustomSearch', CustomSearch);
app.component('CustomTip', CustomTip);
app.component('CustomInput', CustomInput);
app.component('CustomPopover', CustomPopover);
app.component('CustomPicker', CustomPicker);
app.component('CustomTooltip', CustomTooltip);
app.component('SelectIcon', SelectIcon);
app.component('CustomSwitch', CustomSwitch);
app.component('CustomRadio', CustomRadio);
app.component('CustomSelect', CustomSelect);
app.component('SelectActive', SelectActive);
app.component('Swiper', Swiper);
app.component('SwiperSlide', SwiperSlide);
app.component('CustomTabsIcon', CustomTabsIcon);

// 注册全局指令
app.directive('click-outside', clickOutside); //点击外部隐藏元素
app.directive('number-only', NumberOnlyDirective); // 只允许输入数字

// 注册全局方法
app.use(globalMethodsPlugin);

app.config.globalProperties.$message = message;
app.config.globalProperties.$formatFileSize = formatFileSize;
app.config.globalProperties.$formatTime = formatTime;

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

app.use(createPinia());

app.use(router);
app.use(ElementPlus);
app.mount('#app');
